import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { ColorValue, FlexStyle, Pressable } from 'react-native';
import { Platform } from 'utils/platform';

import { WebDiv } from 'components/WebDiv';

type GradientButtonProps = React.ComponentProps<typeof Pressable> & {
  display?: FlexStyle['display'];
  width?: number | string;
  height?: number | string;
  borderColor?: ColorValue;
  borderRadius?: number;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
  useTVFocus?: boolean;
  children: React.ReactNode;
  onBtnFocus?: Function;
  onBtnBlur?: Function;
};

const DEFAULT_BORDER_RADIUS = 25;

const GRADIENT_COLORS = {
  primary: ['#00AD5C', '#6DFFBC'],
  secondary: ['#2240FFF2', '#6DFFBCF2'],
  tertiary: ['#19285e', '#1e2c5e'],
  quaternary: ['#0007384D', '#0007384D'],
};

const NewGradientButton = React.forwardRef<any, GradientButtonProps>(
  (
    {
      gradientVariant = 'primary',
      children,
      style,
      useTVFocus = false,
      borderColor,
      borderRadius = DEFAULT_BORDER_RADIUS,
      display = 'flex',
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const baseOnFocus = () => (Platform.isAndroidTV && useTVFocus ? setIsFocused(true) : null);
    const baseOnBlur = () => (Platform.isAndroidTV && useTVFocus ? setIsFocused(false) : null);

    const widthBtn = typeof rest.width === 'number' ? rest.width : '100%';
    const heightBtn = typeof rest.height === 'number' ? rest.height : 'auto';

    const onFocus = () => {
      baseOnFocus();
      rest.onBtnFocus?.();
    };

    const onBlur = () => {
      baseOnBlur();
      rest.onBtnBlur?.();
    };

    return (
      <WebDiv className={`rounded-6 ${gradientVariant}`}>
        <LinearGradient
          colors={GRADIENT_COLORS[gradientVariant]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius,
            overflow: 'hidden',
            width: widthBtn,
          }}>
          <Pressable
            id="linearGradientButton"
            ref={ref}
            accessibilityRole="button"
            onFocus={onFocus}
            onBlur={onBlur}
            style={(state) => [
              {
                display,
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: borderColor || isFocused ? 'red' : 'transparent',
                borderRadius,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                minWidth: '100%',
                height: heightBtn,
                paddingHorizontal: 20,
              },
              typeof style === 'function' ? style(state) : style,
            ]}
            {...rest}>
            {children}
          </Pressable>
        </LinearGradient>
      </WebDiv>
    );
  }
);

export default NewGradientButton;
